import React, { createContext, useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import ReactGA from "react-ga4";
import { BrowserRouter, useLocation } from "react-router-dom";
import clsx from "clsx";
import reportWebVitals from "./reportWebVitals";

import Routes, { routesFlat, isProductPage } from "@routes";
import { ScrollToTop, getArticles } from "@helpers";
import { newsFilterOptions } from "@helpers/constants";
import { Footer, NavDesktop, NavMobile, CookiesPopup } from "./components";

import { Auth0Provider } from "@auth0/auth0-react";

import "./index.css";
import "react-loading-skeleton/dist/skeleton.css";

// Suppress ResizeObserver errors - this is a known browser quirk that doesn't affect functionality
// This must run BEFORE React initializes to catch errors early
const resizeObserverError = /ResizeObserver.*loop.*completed.*undelivered/i;

// Store original functions
const originalError = console.error;
const originalWarn = console.warn;
const originalLog = console.log;

// Suppress in console.error
console.error = (...args) => {
    const errorMessage = args.map(arg => arg?.toString() || "").join(" ");
    if (resizeObserverError.test(errorMessage)) {
        return;
    }
    originalError.apply(console, args);
};

// Suppress in console.warn
console.warn = (...args) => {
    const warnMessage = args.map(arg => arg?.toString() || "").join(" ");
    if (resizeObserverError.test(warnMessage)) {
        return;
    }
    originalWarn.apply(console, args);
};

// Suppress in console.log (sometimes errors are logged here)
console.log = (...args) => {
    const logMessage = args.map(arg => arg?.toString() || "").join(" ");
    if (resizeObserverError.test(logMessage)) {
        return;
    }
    originalLog.apply(console, args);
};

// Suppress at window error level with capture phase
const handleError = (event) => {
    const errorMessage = 
        event.message || 
        event.error?.message || 
        event.error?.toString() || 
        "";
    if (resizeObserverError.test(errorMessage)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
    }
};

// Add multiple listeners to catch it at different phases
window.addEventListener("error", handleError, true);
window.addEventListener("error", handleError, false);

// Also handle unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
    const errorMessage = 
        event.reason?.message || 
        event.reason?.toString() || 
        "";
    if (resizeObserverError.test(errorMessage)) {
        event.preventDefault();
        return false;
    }
});

// Intercept React error overlay if it exists
if (typeof window !== 'undefined') {
    const originalAddEventListener = window.addEventListener;
    window.addEventListener = function(type, listener, options) {
        if (type === 'error' && typeof listener === 'function') {
            const wrappedListener = function(event) {
                const errorMessage = 
                    event.message || 
                    event.error?.message || 
                    event.error?.toString() || 
                    "";
                if (resizeObserverError.test(errorMessage)) {
                    event.preventDefault();
                    event.stopPropagation();
                    return false;
                }
                return listener.call(this, event);
            };
            return originalAddEventListener.call(this, type, wrappedListener, options);
        }
        return originalAddEventListener.call(this, type, listener, options);
    };
}

ReactGA.initialize("G-KTSEKJ0CT0");

export const CMSContext = createContext(null);
export const NewsContext = createContext(null);

const root = ReactDOM.createRoot(document.getElementById("root"));

const onRedirectCallback = (appState) => {
    window.history.replaceState(
        {},
        document.title,
        appState?.returnTo || window.location.pathname,
    );
};
console.log(window.location.origin);
const providerConfig = {
    domain: process.env.REACT_APP_DOMAIN,
    clientId: process.env.REACT_APP_CLIENT_ID,
    onRedirectCallback,
    authorizationParams: {
        redirect_uri: `${window.location.origin}/callback`,
        ...(process.env.REACT_APP_AUDIENCE
            ? { audience: process.env.REACT_APP_AUDIENCE }
            : null),
    },
};

root.render(
    <Auth0Provider {...providerConfig}>
        <BrowserRouter>
            <Root />
        </BrowserRouter>
    </Auth0Provider>,
);

function Root() {
    const [state, setState] = useState(undefined);

    return (
        <CMSContext.Provider value={{ state, setState }}>
            <Site />
        </CMSContext.Provider>
    );
}

function Site() {
    const location = useLocation();
    const [articles, setArticles] = useState([]);
    const [newsFilter, setNewsFilter] = useState({
        product: newsFilterOptions.product.none,
        type: newsFilterOptions.type.none,
    });

    useEffect(() => {
        getArticles().then((newsfeed) => {
            setArticles(newsfeed);
        });
    }, []);

    const not403 = location.pathname !== routesFlat.noAccess;
    const displayNav = !isProductPage();
    const displayFooter = displayNav && not403;
    
    return (
        <div className={siteBodyStyle}>
            {not403 && (
                <>
                    <CookiesPopup />
                    <NavDesktop />
                    {displayNav && <NavMobile />}
                    <ScrollToTop />
                </>
            )}
            <NewsContext.Provider
                value={{ articles, newsFilter, setNewsFilter }}
            >
                <Routes />
            </NewsContext.Provider>
            {displayFooter && <Footer />}
        </div>
    );
}

const siteBodyStyle = clsx("p-0 w-full h-full font-body bg-background");

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
